import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { motion, AnimatePresence } from './animations';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Initial animation
    setIsVisible(true);

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToForm = () => {
    const formSection = document.getElementById('contact-form');
    if (formSection) {
      formSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <motion.header
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{
        type: "spring",
        stiffness: 100,
        damping: 20,
        delay: 0.2
      }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-background/95 backdrop-blur-md shadow-soft'
          : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo with animation */}
          <motion.div
            className="flex items-center space-x-2"
            initial={{ x: -30, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            <motion.div
              className="w-10 h-10 rounded-full bg-gradient-primary flex items-center justify-center shadow-soft"
              whileHover={{
                scale: 1.1,
                rotate: 10,
                boxShadow: "0 10px 30px -10px rgba(244, 143, 177, 0.5)"
              }}
              whileTap={{ scale: 0.95 }}
              transition={{ type: "spring", stiffness: 400, damping: 17 }}
            >
              <motion.span
                className="text-xl font-bold text-primary-foreground"
                animate={{ rotate: [0, 5, -5, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              >
                E
              </motion.span>
            </motion.div>
            <div>
              <motion.h1
                className="text-xl sm:text-2xl font-bold text-foreground"
                whileHover={{ color: "hsl(var(--primary))" }}
                transition={{ duration: 0.3 }}
              >
                Clinica Estetica Eranza
              </motion.h1>
              <motion.p
                className="text-xs text-muted-foreground hidden sm:block"
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                Tu belleza, nuestra pasion
              </motion.p>
            </div>
          </motion.div>

          {/* CTA Button with animations */}
          <motion.div
            initial={{ x: 30, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.5 }}
          >
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Button
                onClick={scrollToForm}
                className="bg-gradient-primary hover:shadow-glow transition-all duration-300 text-primary-foreground font-medium px-6 py-5 rounded-full relative overflow-hidden group"
              >
                <motion.span
                  className="relative z-10"
                  initial={{ y: 0 }}
                  whileHover={{ y: -2 }}
                >
                  Reserva tu cita
                </motion.span>
                <motion.div
                  className="absolute inset-0 bg-white/20"
                  initial={{ x: "-100%", opacity: 0 }}
                  whileHover={{ x: "100%", opacity: 1 }}
                  transition={{ duration: 0.5 }}
                />
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Animated border line at bottom when scrolled */}
      <AnimatePresence>
        {isScrolled && (
          <motion.div
            className="absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-primary/30 to-transparent"
            initial={{ scaleX: 0, opacity: 0 }}
            animate={{ scaleX: 1, opacity: 1 }}
            exit={{ scaleX: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
          />
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Header;
